import Stripe from 'stripe';

import { notify } from 'src/clients/ApiService';
import {
  ActivationOnlyTaskCode,
  OnboardingStatus,
  OnboardingSurface,
  OnboardingTask,
  OnboardingTaskStatus,
} from 'src/entities/OnboardingStatus';
import {
  canTaskStatusAdvance,
  hasActivationTasks,
} from 'src/features/appBuilder/getting-started-checklist/activationSteps';
import { StudioChecklistTaskStatus } from 'src/features/appBuilder/getting-started-checklist/studioChecklist';
import { TaskCodes } from 'src/legacy/components/HomePage/OnboardingTasksSection/constants';
import type { ClientPreviewSteps } from 'src/store/ui/types';
import { setTrialState } from 'src/store/user/actions';

import { ApiMethods, ApiTags, appAPI } from '.';

export interface PatchDismissalStateParams {
  fields: Partial<OnboardingStatus['fields']>;
}

export interface UpdateStudioTaskStatusParams {
  taskCode: TaskCodes | ActivationOnlyTaskCode;
  status: OnboardingTaskStatus;
}

export interface GetOnboardingTaskStatusParams {
  subscriptions?: Stripe.Subscription[];
  // When true, the studio three-state `status` is derived onto each task (see below).
  // Left off for classic consumers so the response is returned untouched.
  isStudioUser?: boolean;
}

// Studio FTUX mock: the backend does not yet return the studio three-state `status`
// (see the Studio Onboarding & FTUX TDD). Until it does, derive it from the classic
// `done` flag so consumers can read `task.status` off the response directly. Remove
// this once the backend populates `status`.
const withStudioTaskStatus = (task: OnboardingTask): OnboardingTask => ({
  ...task,
  status:
    task.status ??
    (task.done
      ? StudioChecklistTaskStatus.Todo
      : StudioChecklistTaskStatus.Todo),
});

const withStudioTaskStatuses = (
  onboardingStatus: OnboardingStatus,
): OnboardingStatus => ({
  ...onboardingStatus,
  fields: {
    ...onboardingStatus.fields,
    tasks: onboardingStatus.fields.tasks?.map(withStudioTaskStatus),
  },
  additionalFields: {
    ...onboardingStatus.additionalFields,
    tasks: onboardingStatus.additionalFields.tasks?.map(withStudioTaskStatus),
  },
});

export const improvedOnboardingApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    patchDismissalState: build.mutation<
      OnboardingStatus,
      PatchDismissalStateParams
    >({
      query: (entity) => ({
        path: `/onboarding-task-status`,
        method: ApiMethods.patch,
        options: {
          body: {
            ...entity,
          },
        },
      }),
      async onQueryStarted(input, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          improvedOnboardingApi.util.updateQueryData(
            'getOnboardingTaskStatus',
            {},
            (draft) => {
              draft.fields.dismissed = Boolean(input.fields.dismissed);
            },
          ),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          // if there is an error, we need to revert the state
          patch.undo();
          notify({
            status: 'error',
            errorMessage: `Card can not be \`${
              input.fields.dismissed ? 'dismissed' : 'un-dismissed'
            }\``,
            error,
            dispatch,
          });
        }
      },
    }),
    getOnboardingTaskStatus: build.query<
      OnboardingStatus,
      GetOnboardingTaskStatusParams
    >({
      query: () => ({
        path: '/onboarding-task-status',
        method: ApiMethods.get,
        options: {},
      }),
      transformResponse: (
        response: OnboardingStatus,
        _meta,
        arg: GetOnboardingTaskStatusParams,
      ) => (arg?.isStudioUser ? withStudioTaskStatuses(response) : response),
      async onQueryStarted(input, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled;
          dispatch(setTrialState(input.subscriptions, result.data));
        } catch (error) {
          // we want to fail silently because it means that query failed which will happen only if user doesn't have permission
          // and if user doesn't have permission then we don't want to show onboarding tasks to them any way
          console.warn(error);
        }
      },
      providesTags: [ApiTags.onboarding_task_status],
    }),
    // The owner's current checklist as steps, for the client preview card when the
    // link couldn't carry them. Only the onboarding test client may read it.
    getClientPreviewSummary: build.query<ClientPreviewSteps, void>({
      query: () => ({
        path: '/onboarding-task-status/preview-summary',
        method: ApiMethods.get,
        options: {},
      }),
    }),
    // Persists the explicit three-state status of a Studio FTUX task. Invalidating
    // the tag refetches getOnboardingTaskStatus so the checklist reflects the new state.
    // The onboarding-activation checklist is also patched up front in every cached
    // copy, so its steps check off on click instead of after the round trip.
    updateStudioTaskStatus: build.mutation<
      OnboardingStatus,
      UpdateStudioTaskStatusParams
    >({
      query: ({ taskCode, status }) => ({
        path: `/onboarding-task-status/tasks/${taskCode}`,
        method: ApiMethods.put,
        options: {
          body: { status },
        },
      }),
      async onQueryStarted({ taskCode, status }, queryApi) {
        const { dispatch, queryFulfilled } = queryApi;
        const patches = improvedOnboardingApi.util
          .selectCachedArgsForQuery(
            queryApi.getState(),
            'getOnboardingTaskStatus',
          )
          .map((args) =>
            dispatch(
              improvedOnboardingApi.util.updateQueryData(
                'getOnboardingTaskStatus',
                args,
                (draft) => {
                  const { tasks } = draft.additionalFields;
                  if (!hasActivationTasks(tasks)) {
                    return;
                  }
                  const task = tasks?.find(
                    (candidate) => candidate.taskCode === taskCode,
                  );
                  if (task && canTaskStatusAdvance(task.status, status)) {
                    task.status = status;
                    task.done = status === StudioChecklistTaskStatus.Done;
                  }
                },
              ),
            ),
          );

        try {
          await queryFulfilled;
        } catch (error) {
          patches.forEach((patch) => patch.undo());
          console.warn(error);
        }
      },
      invalidatesTags: [ApiTags.onboarding_task_status],
    }),
    // Records a one-time onboarding surface as seen. Every cached copy of the
    // status is patched up front so the surface hides the moment it's dismissed,
    // in each sidebar instance that reads it.
    markOnboardingSurfaceSeen: build.mutation<
      OnboardingStatus,
      OnboardingSurface
    >({
      query: (surface) => ({
        path: `/onboarding-task-status/seen/${surface}`,
        method: ApiMethods.put,
        options: {},
      }),
      async onQueryStarted(surface, queryApi) {
        const { dispatch, queryFulfilled } = queryApi;
        const seenAt = new Date().toISOString();
        const patches = improvedOnboardingApi.util
          .selectCachedArgsForQuery(
            queryApi.getState(),
            'getOnboardingTaskStatus',
          )
          .map((args) =>
            dispatch(
              improvedOnboardingApi.util.updateQueryData(
                'getOnboardingTaskStatus',
                args,
                (draft) => {
                  draft.fields.seenAt = {
                    ...draft.fields.seenAt,
                    [surface]: seenAt,
                  };
                },
              ),
            ),
          );

        try {
          await queryFulfilled;
        } catch (error) {
          patches.forEach((patch) => patch.undo());
          console.warn(error);
        }
      },
    }),
  }),
});

export const {
  usePatchDismissalStateMutation,
  useLazyGetOnboardingTaskStatusQuery,
  useGetOnboardingTaskStatusQuery,
  useGetClientPreviewSummaryQuery,
  useUpdateStudioTaskStatusMutation,
  useMarkOnboardingSurfaceSeenMutation,
} = improvedOnboardingApi;
